import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { authenticatedFetch } from '@/utils/api';

interface UseAuthenticatedFetchOptions {
  skip?: boolean;  // Skip the request if true
  onSuccess?: () => void;  // Called after successful request
  onError?: (error: Error) => void;  // Called on error
}

/**
 * Automatically makes an authenticated API request when data changes
 *
 * @param endpoint - The API endpoint (relative to baseUrl)
 * @param method - HTTP method
 * @param data - Data to send (triggers request when it changes)
 * @param options - Configuration options
 *
 * @example
 * useAuthenticatedFetch(
 *   config.endpoints.reporting,
 *   'POST',
 *   reports,
 *   {
 *     skip: reports.length === 0,
 *     onSuccess: () => dispatch({ type: 'FLUSH_REPORTS' })
 *   }
 * );
 */
export function useAuthenticatedFetch<T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  data: T,
  options: UseAuthenticatedFetchOptions = {}
) {
  const { getIdToken, getCsrfToken } = useAuth();
  const { skip = false, onSuccess, onError } = options;

  // Use ref to track if we're currently making a request
  const isRequestInProgress = useRef(false);

  useEffect(() => {
    // Skip if condition is true or request already in progress
    if (skip || isRequestInProgress.current) return;

    const makeRequest = async () => {
      try {
        isRequestInProgress.current = true;

        const token = getIdToken();
        if (!token) throw new Error('Token not found');

        const csrfToken = getCsrfToken();

        await authenticatedFetch(endpoint, method, token, csrfToken, data);

        onSuccess?.();
      } catch (error) {
        console.error('API request error:', error);
        onError?.(error as Error);
      } finally {
        isRequestInProgress.current = false;
      }
    };

    makeRequest();
  }, [endpoint, method, data, skip, getIdToken, getCsrfToken, onSuccess, onError]);
}
